import { memo, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { CellValue, IdType } from 'react-table'
import type { State } from '@racepointenergy/swc/scs/features/host-states'

import { messages } from '../messages'
import theme from 'theme_'
import { Body12, Body14, BodyPrimary } from 'components/Typography/Labels'
import {
  MobileTableBlock,
  MobileTableBlockCell,
  MobileTableBlockRow,
} from 'components/Table/MobileBlock/styles'
import { getGlyph } from '../LoadStatus/loadIcon'
import { LoadCellWrapper } from '../LoadStatus/styles'
import { ConsumersWrapper, LightSpan } from './styles'
import { formatPower, formatEventMessage } from '../utils'

interface MessageCenterBlockProps {
  values: Record<IdType<State>, CellValue>
  original: Record<IdType<State>, CellValue>
}

interface Consumer {
  uuid: string
  loadName: string
  power_Wh: number
  percentageOfTotal: number
}

export function MessageCenterBlock({
  values,
  original,
}: MessageCenterBlockProps) {
  const { formatMessage, formatDate, formatTime } = useIntl()

  const glyph = useMemo(() => getGlyph(original.image), [original.image])

  const Details = (() => {
    if (!values.Details) {
      return null
    }

    if (values.Details.topConsumers) {
      return (
        <MobileTableBlockCell>
          <ConsumersWrapper>
            {values.Details.topConsumers.map((consumer: Consumer) => (
              <div key={consumer.uuid}>
                {consumer.loadName} &nbsp;
                <LightSpan>
                  {formatPower(consumer.power_Wh, { h: true, absolute: true })}{' '}
                  - <strong>{Math.round(consumer.percentageOfTotal)}%</strong>
                </LightSpan>
              </div>
            ))}
          </ConsumersWrapper>
        </MobileTableBlockCell>
      )
    } else if (values.Details.totalConsumption) {
      return (
        <LightSpan>
          {formatPower(
            values.Details.totalConsumption?.thisPeriod?.power_Wh ||
              values.Details.totalConsumption,
            {
              h: true,
              absolute: true,
            }
          )}
        </LightSpan>
      )
    } else if (values.Details.sceneName) {
      return (
        <MobileTableBlockCell>
          <FormattedMessage {...messages.sceneName} /> &nbsp;
          <LightSpan>{values.Details.sceneName}</LightSpan>
        </MobileTableBlockCell>
      )
    } else if (values.Details.loadName) {
      return (
        <MobileTableBlockCell>
          <FormattedMessage {...messages.loadName} /> &nbsp;
          <LightSpan>{values.Details.loadName}</LightSpan>
        </MobileTableBlockCell>
      )
    } else if (values.Details.maxWattage) {
      return (
        <MobileTableBlockCell>
          <FormattedMessage
            {...messages.circuitExceeded}
            values={{
              loadName: values.Details.name,
              power: values.Details.powerWatts,
              maxPower: values.Details.maxWattage,
            }}
          />
        </MobileTableBlockCell>
      )
    }
    return null
  })()

  return (
    <MobileTableBlock>
      <MobileTableBlockRow>
        <BodyPrimary bold color={theme.colors.otterWarmBlack}>
          <LoadCellWrapper>
            {glyph} {formatEventMessage(values.Event)}
          </LoadCellWrapper>
        </BodyPrimary>
      </MobileTableBlockRow>
      <MobileTableBlockRow>
        <MobileTableBlockCell>
          <Body12 color={theme.colors.darkeyGrey}>
            {formatMessage(messages.headerDate)}
          </Body12>
        </MobileTableBlockCell>
        <MobileTableBlockCell>
          <BodyPrimary color={theme.colors.otterWarmBlack}>
            {formatDate(values.Date * 1000)} &nbsp;
            <LightSpan>{formatTime(values.Date * 1000)}</LightSpan>
          </BodyPrimary>
        </MobileTableBlockCell>
      </MobileTableBlockRow>
      <MobileTableBlockRow>
        <Body14>{Details}</Body14>
      </MobileTableBlockRow>
    </MobileTableBlock>
  )
}

export default memo(MessageCenterBlock)
